import { useState, useEffect, useCallback } from 'react';
import {
  ArrowRightLeft,
  Plus,
  Search,
  X,
  Loader2,
  Edit2,
  Trash2,
  AlertCircle,
} from 'lucide-react';
import { EntityType, ConsolidationMethod, type Entity, type Currency } from '@/types';
import { entitiesApi, currenciesApi } from '@/api/client';
import { useAppStore } from '@/store';

// --- ICP Partner Form Modal ---------------------------------------------------

const DEFAULT_CURRENCIES = ['AED', 'USD', 'EUR', 'GBP', 'JPY', 'SGD', 'SEK', 'CHF', 'AUD', 'CAD', 'CNY', 'INR', 'KES'];

interface FormProps {
  entity?: Entity | null;
  currencies: Currency[];
  onSave: (data: Partial<Entity>) => Promise<void>;
  onClose: () => void;
}

function IcpFormModal({ entity, currencies, onSave, onClose }: FormProps) {
  const [formData, setFormData] = useState({
    code: entity?.code || 'ICP_',
    name: entity?.name || '',
    functional_currency: entity?.functional_currency || 'AED',
    is_active: entity?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!formData.code.trim() || !formData.name.trim()) {
      setError('Code and name are required.');
      return;
    }
    if (!formData.code.toUpperCase().startsWith('ICP_')) {
      setError('ICP partner code must start with ICP_');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave({
        code: formData.code,
        name: formData.name,
        functional_currency: formData.functional_currency,
        is_active: formData.is_active,
        entity_type: EntityType.SUBSIDIARY,
        consolidation_method: ConsolidationMethod.NOT_CONSOLIDATED,
        ownership_pct: 0,
        enable_intercompany: true,
      } as Partial<Entity>);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to save ICP partner.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">
            {entity ? 'Edit ICP Partner' : 'Add ICP Partner'}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">ICP Code</label>
              <input
                className="input-field font-mono"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="ICP_1421"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Currency</label>
              <select
                className="select-field"
                value={formData.functional_currency}
                onChange={(e) => setFormData({ ...formData, functional_currency: e.target.value })}
              >
                {(currencies.length > 0
                  ? currencies.filter((c) => c.is_active).map((c) => c.code)
                  : DEFAULT_CURRENCIES
                ).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Partner Name</label>
            <input
              className="input-field"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Intercompany partner name"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="icp-active"
              type="checkbox"
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            />
            <label htmlFor="icp-active" className="text-sm text-gray-700">Active</label>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} disabled={saving} className="btn-secondary">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="btn-primary">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
            ) : (
              entity ? 'Update Partner' : 'Create Partner'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- ICP Partners Page --------------------------------------------------------

export default function IcpPartners() {
  const [partners, setPartners] = useState<Entity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editEntity, setEditEntity] = useState<Entity | null>(null);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const { addNotification } = useAppStore();

  const loadPartners = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [response, currenciesRes] = await Promise.all([
        entitiesApi.list({ page: 1, page_size: 5000, exclude_icp: false, only_icp: true }),
        currenciesApi.list().catch(() => [] as Currency[]),
      ]);
      setPartners(response.items);
      setCurrencies(currenciesRes);
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      setError(apiErr.detail || apiErr.message || 'Failed to load ICP partners');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  const filteredPartners = partners.filter(
    (e) =>
      e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.code.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleSave = async (data: Partial<Entity>) => {
    if (editEntity) {
      await entitiesApi.update(editEntity.id, data);
      addNotification({ type: 'success', title: 'ICP Partner Updated', message: `${data.name || editEntity.name} has been updated.` });
    } else {
      await entitiesApi.create(data);
      addNotification({ type: 'success', title: 'ICP Partner Created', message: `${data.name} has been created.` });
    }
    setShowModal(false);
    setEditEntity(null);
    await loadPartners();
  };

  const [deleteTarget, setDeleteTarget] = useState<Entity | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await entitiesApi.delete(deleteTarget.id);
      addNotification({ type: 'success', title: 'ICP Partner Deleted', message: `${deleteTarget.name} has been removed.` });
      setDeleteTarget(null);
      await loadPartners();
    } catch (err: unknown) {
      const apiErr = err as { detail?: string; message?: string };
      addNotification({ type: 'error', title: 'Delete Failed', message: apiErr.detail || 'Failed to delete ICP partner.' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">ICP Partners</h1>
          <p className="text-sm text-gray-500 mt-1">Manage intercompany partners used for IC matching and elimination</p>
        </div>
        <button
          onClick={() => { setEditEntity(null); setShowModal(true); }}
          className="btn-primary"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add ICP Partner
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          className="input-field pl-9"
          placeholder="Search by name or ICP code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Error state */}
      {error && (
        <div className="card p-8 text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-red-300 mb-3" />
          <h3 className="text-base font-medium text-gray-900">Failed to load ICP partners</h3>
          <p className="text-sm text-red-600 mt-1">{error}</p>
          <button onClick={loadPartners} className="btn-primary mt-4">Retry</button>
        </div>
      )}

      {/* Content */}
      {!error && isLoading ? (
        <div className="card">
          <div className="p-6 space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="skeleton w-24 h-4" />
                <div className="skeleton w-48 h-4" />
                <div className="skeleton w-16 h-4" />
                <div className="skeleton w-16 h-5 rounded-full" />
              </div>
            ))}
          </div>
        </div>
      ) : !error ? (
        <div className="card overflow-hidden">
          {filteredPartners.length === 0 ? (
            <div className="py-16 text-center">
              <ArrowRightLeft className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <h3 className="text-sm font-medium text-gray-900">No ICP partners found</h3>
              <p className="text-sm text-gray-500 mt-1">
                {searchQuery ? 'Try adjusting your search query' : 'Add intercompany partners to get started'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="table-header">ICP Code</th>
                    <th className="table-header">Partner Name</th>
                    <th className="table-header">Currency</th>
                    <th className="table-header">Status</th>
                    <th className="table-header w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {filteredPartners.map((partner) => (
                    <tr key={partner.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="table-cell">
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-primary-50 text-primary-700 font-mono text-xs font-medium">
                          {partner.code}
                        </span>
                      </td>
                      <td className="table-cell font-medium text-gray-900">{partner.name}</td>
                      <td className="table-cell">
                        <span className="badge bg-gray-100 text-gray-700">{partner.functional_currency}</span>
                      </td>
                      <td className="table-cell">
                        {partner.is_active ? (
                          <span className="badge bg-green-50 text-green-700">Active</span>
                        ) : (
                          <span className="badge bg-gray-100 text-gray-500">Inactive</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => { setEditEntity(partner); setShowModal(true); }}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit ICP partner"
                          >
                            <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(partner)}
                            className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete ICP partner"
                          >
                            <Trash2 className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* Summary */}
      {!isLoading && !error && partners.length > 0 && (
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" />
            {partners.length} ICP partners
          </span>
          <span>{partners.filter((p) => p.is_active).length} active</span>
        </div>
      )}

      {/* Form Modal */}
      {showModal && (
        <IcpFormModal
          entity={editEntity}
          currencies={currencies}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditEntity(null); }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 py-5">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Delete ICP Partner</h3>
              <p className="text-sm text-gray-600">
                Are you sure you want to delete <strong>&quot;{deleteTarget.name}&quot;</strong> ({deleteTarget.code})?
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button onClick={() => setDeleteTarget(null)} disabled={deleting} className="btn-secondary">Cancel</button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : <><Trash2 className="w-4 h-4 mr-2" />Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
